import { supabase } from '@/integrations/supabase/client';

export interface FamilyGroup {
  id: string;
  name: string;
  description: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

export interface FamilyMember {
  id: string;
  family_group_id: string;
  user_id?: string;
  email?: string;
  phone?: string;
  name?: string;
  role: string;
  status: string;
  invited_at: string;
  joined_at?: string;
}

export const familyService = {
  async getUserFamilyGroups(): Promise<FamilyGroup[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    console.log('Loading family groups for user:', user.id, user.email);

    try {
      // Get owned groups
      const { data: ownedGroups, error: ownedError } = await supabase
        .from('family_groups')
        .select('*')
        .eq('owner_id', user.id);

      console.log('Owned groups query result:', { ownedGroups, ownedError });

      if (ownedError) {
        console.error('Owned groups error:', ownedError);
        throw ownedError;
      }

      // Get groups where user is a member
      const { data: membershipData, error: membershipError } = await supabase
        .from('family_memberships')
        .select('family_group_id')
        .eq('status', 'active')
        .or(`user_id.eq.${user.id}${user.email ? `,email.eq.${user.email}` : ''}`);

      console.log('Membership query result:', { membershipData, membershipError });

      if (membershipError) {
        console.error('Membership error:', membershipError);
        throw membershipError;
      }

      // Get the family groups for those memberships
      let memberGroups: FamilyGroup[] = [];
      if (membershipData && membershipData.length > 0) {
        const groupIds = membershipData.map(m => m.family_group_id);
        const { data: groupsData, error: groupsError } = await supabase
          .from('family_groups')
          .select('*')
          .in('id', groupIds);

        console.log('Member groups query result:', { groupsData, groupsError });

        if (groupsError) {
          console.error('Member groups error:', groupsError);
          throw groupsError;
        }

        memberGroups = groupsData || [];
      }

      // Combine and deduplicate
      const allGroups = [...(ownedGroups || [])];
      const seenIds = new Set(ownedGroups?.map(g => g.id) || []);

      memberGroups.forEach(group => {
        if (!seenIds.has(group.id)) {
          allGroups.push(group);
        }
      });

      console.log('Final combined groups:', allGroups);
      return allGroups;
    } catch (error) {
      console.error('Error in getUserFamilyGroups:', error);
      throw error;
    }
  },

  async getFamilyMembers(familyGroupId: string): Promise<FamilyMember[]> {
    const { data, error } = await supabase
      .from('family_memberships')
      .select('*')
      .eq('family_group_id', familyGroupId)
      .order('created_at');

    if (error) throw error;
    return data || [];
  },

  async createFamilyGroup(name: string, description?: string): Promise<FamilyGroup> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('family_groups')
      .insert({
        name,
        description: description || `${name} family group`,
        owner_id: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async addFamilyMember(familyGroupId: string, memberData: {
    name?: string;
    email?: string;
    phone?: string;
    role?: string;
  }): Promise<FamilyMember> {
    const { data, error } = await supabase
      .from('family_memberships')
      .insert({
        family_group_id: familyGroupId,
        name: memberData.name,
        email: memberData.email,
        phone: memberData.phone,
        role: memberData.role || 'member',
        status: 'pending',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getFamilyMembersAsGuestOptions(familyGroupId: string): Promise<Array<{
    name?: string;
    email?: string;
    phone?: string;
  }>> {
    const members = await this.getFamilyMembers(familyGroupId);
    
    return members
      .filter(member => member.status === 'active' && (member.email || member.phone))
      .map(member => ({
        name: member.name,
        email: member.email,
        phone: member.phone,
      }));
  }
};